"use client";

import { useMemo, useState } from "react";
import { useNotifications } from "@/hooks/use-notifications";
import {
  usePendingInvitations,
  acceptInvitation,
  declineInvitation,
} from "@/hooks/use-collaboration";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Bell,
  Trophy,
  TrendingUp,
  UserPlus,
  UserMinus,
  UserCheck,
  UserX,
  ShieldCheck,
  FolderPlus,
  CheckCheck,
  Check,
  X,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useRouter } from "next/navigation";

function getNotificationIcon(type: string) {
  switch (type) {
    case "collaboration_invite":
      return <UserPlus size={16} className="text-blue-500" />;
    case "invite_accepted":
      return <UserCheck size={16} className="text-emerald-500" />;
    case "invite_declined":
      return <UserX size={16} className="text-red-500" />;
    case "member_removed":
      return <UserMinus size={16} className="text-red-500" />;
    case "role_changed":
      return <ShieldCheck size={16} className="text-purple-500" />;
    case "items_added":
      return <FolderPlus size={16} className="text-blue-500" />;
    case "badge_earned":
      return <Trophy size={16} className="text-amber-500" />;
    case "level_up":
      return <TrendingUp size={16} className="text-emerald-500" />;
    default:
      return <Bell size={16} className="text-zinc-500" />;
  }
}

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diff = now - date;

  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;

  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

type NotificationItem = {
  id: string;
  type: string;
  title: string;
  body: string;
  read: boolean;
  createdAt: string;
  referenceId?: string | null;
  referenceType?: string | null;
  metadata?: Record<string, unknown> | null;
};

function InviteActions({
  invitationId,
  notificationId,
  onActioned,
  onMarkRead,
}: {
  invitationId: string;
  notificationId: string;
  onActioned: () => void;
  onMarkRead: (id: string) => void;
}) {
  const router = useRouter();
  const [processing, setProcessing] = useState<"accept" | "decline" | null>(
    null,
  );

  const handleAccept = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setProcessing("accept");
    const result = await acceptInvitation(invitationId);
    setProcessing(null);

    if (result.ok) {
      toast.success("Invitation accepted!");
      onMarkRead(notificationId);
      onActioned();
      if (result.collectionId) {
        router.push(`/collections/${result.collectionId}`);
      }
    } else {
      toast.error(result.error || "Failed to accept invitation");
    }
  };

  const handleDecline = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setProcessing("decline");
    const result = await declineInvitation(invitationId);
    setProcessing(null);

    if (result.ok) {
      toast.success("Invitation declined");
      onMarkRead(notificationId);
      onActioned();
    } else {
      toast.error(result.error || "Failed to decline invitation");
    }
  };

  const isDisabled = processing !== null;

  return (
    <div className="flex gap-2 mt-3">
      <button
        onClick={handleAccept}
        disabled={isDisabled}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-all active:scale-95 shadow-[0_0_15px_-5px_rgba(var(--primary),0.4)]"
      >
        {processing === "accept" ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Check className="w-3.5 h-3.5 stroke-[3]" />
        )}
        Accept
      </button>
      <button
        onClick={handleDecline}
        disabled={isDisabled}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] rounded-lg border border-white/[0.1] bg-white/[0.03] text-zinc-400 hover:text-white hover:bg-white/[0.08] disabled:opacity-50 transition-all active:scale-95"
      >
        {processing === "decline" ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <X className="w-3.5 h-3.5 stroke-[3]" />
        )}
        Decline
      </button>
    </div>
  );
}

function NotificationRow({
  notification,
  onRead,
  isPendingInvite,
  onInviteActioned,
}: {
  notification: NotificationItem;
  onRead: (id: string) => void;
  isPendingInvite: boolean;
  onInviteActioned: () => void;
}) {
  const invitationId = notification.metadata?.invitationId as
    | string
    | undefined;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => {
        if (!notification.read) onRead(notification.id);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !notification.read) onRead(notification.id);
      }}
      className={cn(
        "w-full flex items-start gap-4 px-5 py-4 text-left transition-all duration-300 hover:bg-white/[0.05] cursor-pointer group relative",
        !notification.read && "bg-primary/[0.03]",
      )}
    >
      {!notification.read && (
        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary shadow-[0_0_8px_rgba(var(--primary),0.8)]" />
      )}
      
      <div className={cn(
        "mt-0.5 flex-shrink-0 rounded-xl p-2 border transition-colors duration-300",
        !notification.read ? "bg-white/[0.08] border-white/[0.15]" : "bg-black/20 border-white/[0.05]"
      )}>
        {getNotificationIcon(notification.type)}
      </div>
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            "text-[15px] leading-snug transition-colors duration-300",
            !notification.read
              ? "font-bold text-white"
              : "font-medium text-zinc-400 group-hover:text-zinc-300",
          )}
        >
          {notification.title}
        </p>
        <p className="text-[13px] text-zinc-500 mt-1 line-clamp-2 leading-relaxed font-light">
          {notification.body}
        </p>
        <div className="flex items-center gap-2 mt-2">
          <p
            className={cn(
              "text-[11px] font-black uppercase tracking-[0.1em]",
              !notification.read
                ? "text-primary"
                : "text-zinc-600",
            )}
          >
            {formatRelativeTime(notification.createdAt)}
          </p>
          
          {!notification.read && (
            <div className="w-1 h-1 rounded-full bg-primary animate-pulse shadow-[0_0_5px_rgba(var(--primary),0.5)]" />
          )}
        </div>

        {isPendingInvite && invitationId && (
          <InviteActions
            invitationId={invitationId}
            notificationId={notification.id}
            onActioned={onInviteActioned}
            onMarkRead={onRead}
          />
        )}
      </div>
    </div>
  );
}

export function NotificationCenter() {
  const [filter, setFilter] = useState<"all" | "unread">("all");

  const {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    mutate: mutateNotifications,
  } = useNotifications({ unreadOnly: filter === "unread" });

  const { invitations, mutate: mutateInvitations } = usePendingInvitations();

  const pendingInvitationIds = useMemo(
    () => new Set(invitations.map((inv) => inv.id)),
    [invitations],
  );

  const handleInviteActioned = () => {
    mutateInvitations();
    mutateNotifications();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="relative p-2 rounded-xl text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.05] transition-all duration-300 border border-white/[0.05] hover:border-white/[0.15]"
          aria-label="Notifications"
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 flex h-2 w-2 items-center justify-center rounded-full bg-primary shadow-[0_0_8px_rgba(var(--primary),0.8)]" />
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={12}
        className="w-[400px] p-0 overflow-hidden bg-[#0c0c0e] border border-white/[0.1] border-t-white/[0.15] rounded-[2rem] shadow-2xl"
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-white/[0.08] bg-black/40">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-white uppercase tracking-widest">
              Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  markAllAsRead();
                }}
                className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-zinc-500 hover:text-primary transition-colors"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Mark all read
              </button>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                setFilter("all");
              }}
              className={cn(
                "px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] rounded-full transition-all duration-300 border",
                filter === "all"
                  ? "bg-white text-black border-white shadow-xl"
                  : "bg-white/[0.03] text-zinc-500 border-white/[0.08] hover:text-zinc-200 hover:bg-white/[0.08]",
              )}
            >
              All
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                setFilter("unread");
              }}
              className={cn(
                "px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] rounded-full transition-all duration-300 border",
                filter === "unread"
                  ? "bg-white text-black border-white shadow-xl"
                  : "bg-white/[0.03] text-zinc-500 border-white/[0.08] hover:text-zinc-200 hover:bg-white/[0.08]",
              )}
            >
              Unread{unreadCount > 0 ? ` (${unreadCount})` : ""}
            </button>
          </div>
        </div>

        {/* Notification list */}
        <div className="overflow-y-auto max-h-[450px] no-scrollbar">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 text-zinc-700 animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 px-8 text-center">
              <div className="w-16 h-16 bg-white/[0.02] border border-white/[0.08] rounded-2xl flex items-center justify-center mb-5 shadow-inner">
                <Bell className="w-7 h-7 text-zinc-800" />
              </div>
              <p className="text-base font-bold text-white mb-1">
                {filter === "unread"
                  ? "Neural channels clear"
                  : "No signals received"}
              </p>
              <p className="text-[12px] text-zinc-600 uppercase tracking-[0.1em] font-medium">
                {filter === "unread"
                  ? "Inbox zero achieved"
                  : "Awaiting account activity"}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/[0.05]">
              {notifications.map((notification: NotificationItem) => {
                const invitationId = notification.metadata?.invitationId as
                  | string
                  | undefined;
                const isPendingInvite =
                  notification.type === "collaboration_invite" &&
                  !!invitationId &&
                  pendingInvitationIds.has(invitationId);

                return (
                  <NotificationRow
                    key={notification.id}
                    notification={notification}
                    onRead={markAsRead}
                    isPendingInvite={isPendingInvite}
                    onInviteActioned={handleInviteActioned}
                  />
                );
              })}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
